import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ExternalLink, 
  Check, 
  X as XIcon,
  Scale,
  Globe,
  Shield,
  DollarSign,
  Zap,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  Info,
  Building2,
  BadgeCheck,
  Award,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { featureConfig } from '@/data/platforms';
import { useWizard } from '@/components/wizard/WizardContext';
import { DataTypeLabel, ConfidenceIndicator } from '@/components/shared/ConfidenceIndicators';
import { SustainabilitySection } from '@/components/platform/SustainabilitySection';
import { analyticsApi, platformsApi } from '@/services/api';
import { cn } from '@/lib/utils';
import { SEO, PlatformStructuredData } from '@/components/SEO';

// Vendor Badge Component
const VendorBadge = ({ badge }) => {
  if (!badge) return null;
  
  const colorClasses = {
    gold: 'bg-amber-100 text-amber-700 border-amber-300',
    blue: 'bg-blue-100 text-blue-700 border-blue-300',
    gray: 'bg-muted text-muted-foreground border-muted-foreground/20',
  };
  
  return (
    <Badge 
      variant="outline" 
      className={cn(
        'text-xs font-medium gap-1',
        colorClasses[badge.badge_color] || colorClasses.gray
      )}
    >
      {badge.tier === 'enterprise' ? (
        <Award className="w-3 h-3" />
      ) : (
        <BadgeCheck className="w-3 h-3" />
      )}
      {badge.badge_text}
    </Badge>
  );
};

// Recently Updated Badge Component
const RecentlyUpdatedBadge = ({ updatedAt }) => {
  if (!updatedAt) return null;
  
  const updateDate = new Date(updatedAt);
  const now = new Date();
  const daysDiff = Math.floor((now - updateDate) / (1000 * 60 * 60 * 24));
  
  // Only show badge if updated within last 30 days
  if (daysDiff > 30) return null;
  
  return (
    <Badge 
      variant="outline" 
      className="text-xs font-medium gap-1 bg-emerald-50 text-emerald-700 border-emerald-300"
    >
      <RefreshCw className="w-3 h-3" />
      Recently Updated
    </Badge>
  );
};

const FeatureItem = ({ available, label }) => (
  <div className="flex items-center gap-2 py-1.5">
    {available ? (
      <Check className="w-4 h-4 text-success flex-shrink-0" />
    ) : (
      <XIcon className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
    )}
    <span className={cn(
      'text-sm',
      available ? 'text-foreground' : 'text-muted-foreground'
    )}>
      {label}
    </span>
  </div>
);

const DataBadge = ({ value, positive = true }) => (
  <Badge 
    variant="outline" 
    className={cn(
      'text-xs',
      positive ? 'border-success/30 bg-success-light text-success' : 'border-border'
    )}
  >
    {value ? <Check className="w-3 h-3 mr-1" /> : <XIcon className="w-3 h-3 mr-1" />}
    {value ? 'Yes' : 'No'}
  </Badge>
);

const CostIndicator = ({ level }) => {
  const bands = [
    { level: 1, label: 'Free / Low Cost', color: 'text-success' },
    { level: 2, label: 'Moderate (£)', color: 'text-primary' },
    { level: 3, label: 'Higher (££)', color: 'text-amber-600' },
    { level: 4, label: 'Enterprise (£££)', color: 'text-destructive' },
  ];
  const band = bands.find(b => b.level === level) || bands[0];
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            className={cn(
              'w-2 h-4 rounded-sm',
              i <= level ? 'bg-primary' : 'bg-muted'
            )}
          />
        ))}
      </div>
      <span className={cn('text-sm font-medium', band.color)}>{band.label}</span>
    </div>
  );
};

export default function PlatformDetailPage() {
  const { platformId } = useParams();
  const navigate = useNavigate();
  const { toggleComparison, selectedForComparison, results, answers, getPlatformById, platformsLoading } = useWizard();
  
  // State for directly fetched platform (when not found in context)
  const [fetchedPlatform, setFetchedPlatform] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Try to get platform from context first
  const contextPlatform = getPlatformById(platformId);
  
  // Fetch platform directly from API if not in context
  useEffect(() => {
    const fetchPlatform = async () => {
      // If already have it from context, no need to fetch
      if (contextPlatform) {
        setLoading(false);
        return;
      }
      
      // If context is still loading, wait
      if (platformsLoading) {
        return;
      }
      
      // Context loaded but platform not found - fetch directly
      setLoading(true);
      setError(null);
      
      try {
        const data = await platformsApi.get(platformId);
        if (data && data.id) {
          setFetchedPlatform(data);
        } else {
          setError('Platform not found');
        }
      } catch (err) {
        console.error('Failed to fetch platform:', err);
        setError('Platform not found');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlatform();
  }, [platformId, contextPlatform, platformsLoading]);
  
  // Use context platform if available, otherwise use fetched platform
  const platform = contextPlatform || fetchedPlatform;
  
  // Track platform view (both general and vendor-specific)
  useEffect(() => {
    if (platform) {
      // General analytics (for admin dashboard)
      analyticsApi.track('platform_view', { 
        platform_id: platformId,
        platform_name: platform.name 
      }).catch(() => {});
      
      // Vendor-specific analytics (for vendor dashboard)
      analyticsApi.trackVendor('profile_view', platformId, 'profile_page');
    }
  }, [platformId, platform]);
  
  // Show loading state
  if (loading || platformsLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading platform details...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  if (!platform || error) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-foreground mb-2">Platform not found</h1>
            <p className="text-muted-foreground mb-4">The platform you&apos;re looking for doesn&apos;t exist.</p>
            <Button onClick={() => navigate('/')}>Return to results</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Check if we have suitability data from results
  const resultData = results.find(r => r.id === platformId);
  const hasSuitabilityData = resultData && answers && Object.keys(answers).length > 0;
  
  const isFictional = platform.tags?.includes('fictional');
  const isSelected = selectedForComparison.includes(platformId);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO platform={platform} />
      <PlatformStructuredData platform={platform} />
      <Header />
      
      <main className="flex-1 w-full">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          {/* Back navigation */}
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6 -ml-2 gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>

          {/* Header section */}
          <div className="animate-fade-in">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h1 className="text-3xl font-semibold text-foreground">{platform.name}</h1>
                  {platform.vendor_badge && (
                    <VendorBadge badge={platform.vendor_badge} />
                  )}
                  <RecentlyUpdatedBadge updatedAt={platform.updatedAt} />
                  {isFictional && (
                    <Badge variant="outline" className="text-xs">Example Platform</Badge>
                  )}
                </div>
                <p className="text-lg text-muted-foreground">{platform.provider}</p>
                {platform.is_vendor_managed && !platform.vendor_badge && (
                  <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    Vendor-provided information
                  </p>
                )}
              </div>
              
              {/* Quick actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant={isSelected ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleComparison(platformId)}
                  className="gap-1.5"
                >
                  <Scale className="w-4 h-4" />
                  {isSelected ? 'Selected' : 'Compare'}
                </Button>
                {platform.links?.website && platform.links.website !== '#' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Track outbound click for vendor analytics
                      analyticsApi.trackVendor('outbound_click', platformId, 'profile_page');
                      window.open(platform.links.website, '_blank', 'noopener,noreferrer');
                    }}
                    className="gap-1.5"
                  >
                    <Globe className="w-4 h-4" />
                    Website
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>

            {/* Summary - Factual */}
            <Card className="mb-6">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-muted-foreground">ABOUT</span>
                  <ConfidenceIndicator level="vendor" showLabel />
                </div>
                <p className="text-foreground leading-relaxed">{platform.description}</p>
              </CardContent>
            </Card>

            {/* Suitability section - INTERPRETATION (only show if we have user answers) */}
            {hasSuitabilityData && (
              <Card className="mb-6 border-accent/20 bg-accent/5">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Info className="w-5 h-5 text-accent" />
                      Based on your answers
                    </CardTitle>
                    <DataTypeLabel type="interpretation" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Badge 
                      className={cn(
                        'px-3 py-1',
                        resultData.suitability === 'suitable' && 'bg-success text-success-foreground',
                        resultData.suitability === 'caution' && 'bg-warning text-warning-foreground',
                        resultData.suitability === 'not-recommended' && 'bg-destructive text-destructive-foreground'
                      )}
                    >
                      {resultData.suitability === 'suitable' && 'Suitable'}
                      {resultData.suitability === 'caution' && 'Use with caution'}
                      {resultData.suitability === 'not-recommended' && 'Not recommended'}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Match score: {Math.round(resultData.percentage)}%
                    </span>
                  </div>
                  
                  {resultData.reasons.suitable.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-success flex items-center gap-1 mb-2">
                        <ThumbsUp className="w-4 h-4" />
                        Why this might suit you
                      </p>
                      <ul className="text-sm text-muted-foreground space-y-1 pl-5">
                        {resultData.reasons.suitable.map((reason, i) => (
                          <li key={i} className="list-disc">{reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {resultData.reasons.concerns.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-amber-600 flex items-center gap-1 mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        What to be aware of
                      </p>
                      <ul className="text-sm text-muted-foreground space-y-1 pl-5">
                        {resultData.reasons.concerns.map((concern, i) => (
                          <li key={i} className="list-disc">{concern}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <p className="text-xs text-muted-foreground pt-2 border-t border-accent/20">
                    This assessment is based on how your stated needs align with this platform's characteristics. 
                    It is not a recommendation or endorsement.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* FACTUAL SECTIONS */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-lg font-semibold text-foreground">Platform Information</h2>
                <DataTypeLabel type="fact" />
              </div>
            </div>

            {/* Main content grid - Factual */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Features */}
              {platform.features && Object.keys(platform.features).length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="w-5 h-5 text-primary" />
                      Features
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-4">
                      {Object.entries(platform.features).map(([key, value]) => (
                        <FeatureItem 
                          key={key}
                          available={value}
                          label={featureConfig[key]?.label || key}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pricing */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-primary" />
                    Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {platform.costBand && <CostIndicator level={platform.costBand} />}
                  
                  {(platform.pricing?.hasFreeeTier || platform.pricing?.hasFreeTier) && (
                    <div>
                      <p className="text-sm font-medium text-success">Free tier available</p>
                      <p className="text-sm text-muted-foreground">{platform.pricing?.freeTierLimits}</p>
                    </div>
                  )}
                  
                  {platform.pricing?.paidPlans && (
                    <div>
                      <p className="text-sm font-medium text-foreground mb-2">Paid plans</p>
                      {Array.isArray(platform.pricing.paidPlans) ? (
                        <div className="space-y-2">
                          {platform.pricing.paidPlans.map((plan, i) => (
                            <div key={i} className="text-sm p-2 bg-muted/50 rounded-lg">
                              <div className="flex justify-between items-center mb-1">
                                <span className="font-medium">{plan.name}</span>
                                <span className="text-primary">{plan.price}</span>
                              </div>
                              <p className="text-muted-foreground text-xs">{plan.features}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">{platform.pricing.paidPlans}</p>
                      )}
                    </div>
                  )}
                  
                  {platform.pricing.apiPricing && (
                    <div>
                      <p className="text-sm font-medium text-foreground">API pricing</p>
                      <p className="text-sm text-muted-foreground">{platform.pricing.apiPricing}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Data Handling - Factual */}
            {platform.dataHandling && (
              <Card className="mb-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Data Handling & Security
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      {platform.dataHandling.dataRetention && (
                        <div>
                          <p className="text-sm font-medium text-foreground mb-1">Data retention</p>
                          <p className="text-sm text-muted-foreground">{platform.dataHandling.dataRetention}</p>
                        </div>
                      )}
                      {platform.dataHandling.dataLocation && (
                        <div>
                          <p className="text-sm font-medium text-foreground mb-1">Data location</p>
                          <p className="text-sm text-muted-foreground">{platform.dataHandling.dataLocation}</p>
                        </div>
                      )}
                      {platform.dataHandling.optOutAvailable !== undefined && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm">Opt-out available:</span>
                          <DataBadge value={platform.dataHandling.optOutAvailable} />
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-foreground">Compliance certifications</p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant={platform.dataHandling.gdprCompliant ? 'default' : 'outline'} className="text-xs">
                          GDPR {platform.dataHandling.gdprCompliant ? '✓' : '—'}
                        </Badge>
                        <Badge variant={platform.dataHandling.hipaaCompliant ? 'default' : 'outline'} className="text-xs">
                          HIPAA {platform.dataHandling.hipaaCompliant ? '✓' : '—'}
                        </Badge>
                        <Badge variant={platform.dataHandling.soc2Certified ? 'default' : 'outline'} className="text-xs">
                          SOC 2 {platform.dataHandling.soc2Certified ? '✓' : '—'}
                        </Badge>
                      </div>
                      {platform.dataHandling.enterpriseDataProtection !== undefined && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm">Enterprise data protection:</span>
                          <DataBadge value={platform.dataHandling.enterpriseDataProtection} />
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* INTERPRETIVE SECTIONS */}
            {(platform.pros?.length > 0 || platform.cons?.length > 0 || platform.bestFor?.length > 0 || platform.notIdealFor?.length > 0) && (
              <>
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-4">
                    <h2 className="text-lg font-semibold text-foreground">Our Assessment</h2>
                    <DataTypeLabel type="interpretation" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    The following sections reflect our independent analysis based on research and testing. 
                    These are interpretive judgments, not statements from the vendor.
                  </p>
                </div>

                {/* Pros and Cons - Interpretive */}
                {(platform.pros?.length > 0 || platform.cons?.length > 0) && (
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {platform.pros?.length > 0 && (
                      <Card className="border-accent/10">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2 text-success">
                            <ThumbsUp className="w-5 h-5" />
                            Strengths
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {platform.pros.map((pro, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <Check className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                                <span className="text-foreground">{pro}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}

                    {platform.cons?.length > 0 && (
                      <Card className="border-accent/10">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2 text-amber-600">
                            <ThumbsDown className="w-5 h-5" />
                            Limitations
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {platform.cons.map((con, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                <span className="text-foreground">{con}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {/* Best for / Not ideal for - Interpretive */}
                {(platform.bestFor?.length > 0 || platform.notIdealFor?.length > 0) && (
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {platform.bestFor?.length > 0 && (
                      <Card className="border-accent/10">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-primary" />
                            Best suited for
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {platform.bestFor.map((item, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                <span className="text-foreground">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}

                    {platform.notIdealFor?.length > 0 && (
                      <Card className="border-accent/10">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-muted-foreground" />
                            May not be ideal for
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {platform.notIdealFor.map((item, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <XIcon className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                <span className="text-muted-foreground">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Sustainability Section - Vendor Provided */}
            <SustainabilitySection platformId={platformId} />

            {/* Disclaimer */}
            <Card className="bg-muted/50 border-border/50">
              <CardContent className="flex items-start gap-3 py-4">
                <Info className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">About this information</p>
                  <p>
                    {isFictional 
                      ? 'This is a fictional example platform for demonstration purposes. It does not represent a real product or service.'
                      : 'Platform information is sourced from vendor documentation. Assessment sections reflect our independent analysis. Features, pricing, and policies may change — please verify current details directly with the provider before making decisions.'
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
